/**
 * The operations that are running now (DESIGN UD-11, UX-SPEC §15a): one signal the shell's status bar reads. An operation
 * that can take about a second or more registers here when it starts and leaves when it ends; the status bar shows its
 * indicator after 400 ms and, when one can be made, a time estimate (`estimate.ts`). Page-level states (the analysis
 * panel, restoring) draw their own indicator from the phase signals and do not register.
 *
 * `startedAt` is on the `performance.now()` clock.
 */
import { signal } from '@preact/signals';
import { INDICATOR_DELAY_MS } from './estimate.ts';

export interface WorkItem {
	id: string;
	label: string;
	startedAt: number;
	/** 0..1, or null when the operation reports no progress. */
	fraction: number | null;
	/** The fraction already done when the item started (a resumed job). */
	startFraction: number;
	/** The expected total time from the reported rates, when there is a basis for one. */
	priorMs: number | null;
	/** A second line: counts, the Batch API's own numbers. */
	detail: string | null;
	/** A line shown instead of a time estimate when none can be made. */
	hint: string | null;
	/**
	 * How long before the indicator shows (default 400 ms). 0 for work that blocks the main thread: a timer cannot fire
	 * during it, so the indicator must already be on the page when it starts. No operation does that now (the local pass
	 * moved into the bench worker, UD-12).
	 */
	delayMs: number;
	/** Opens a fuller status view (a dialog) when the indicator is clicked (UD-20). */
	onOpen?: () => void;
}

export const work = signal<readonly WorkItem[]>([]);

export type WorkStart = Partial<
	Pick<
		WorkItem,
		'fraction' | 'startFraction' | 'priorMs' | 'detail' | 'hint' | 'delayMs' | 'onOpen'
	>
>;

export function beginWork(id: string, label: string, opts: WorkStart = {}): void {
	const item: WorkItem = {
		id,
		label,
		startedAt: performance.now(),
		fraction: opts.fraction ?? null,
		startFraction: opts.startFraction ?? 0,
		priorMs: opts.priorMs ?? null,
		detail: opts.detail ?? null,
		hint: opts.hint ?? null,
		delayMs: opts.delayMs ?? INDICATOR_DELAY_MS,
		...(opts.onOpen ? { onOpen: opts.onOpen } : {}),
	};
	work.value = [...work.value.filter((w) => w.id !== id), item];
}

export function updateWork(id: string, patch: WorkStart & { label?: string }): void {
	work.value = work.value.map((w) => (w.id === id ? { ...w, ...patch } : w));
}

export function endWork(id: string): void {
	if (work.value.some((w) => w.id === id)) work.value = work.value.filter((w) => w.id !== id);
}

/** Runs `fn` as a registered operation; the item always leaves, whether `fn` resolves or throws. */
export async function withWork<T>(
	id: string,
	label: string,
	opts: WorkStart,
	fn: () => Promise<T>
): Promise<T> {
	beginWork(id, label, opts);
	try {
		return await fn();
	} finally {
		endWork(id);
	}
}

/** One animation frame, then the next macrotask: lets the indicator paint before a long synchronous task starts. */
export function nextPaint(): Promise<void> {
	return new Promise((resolve) => {
		const done = () => setTimeout(resolve, 0);
		if (typeof requestAnimationFrame === 'function') requestAnimationFrame(done);
		else done();
	});
}
